import { useEffect, useState } from "react";
import {
    CCard,
    CCardBody,
    CCardTitle,
    CButton,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CBadge,
} from "@coreui/react";
import { toast } from "react-toastify";
import CookieFunctions from "../functions/CookieFunctions";
import globalFunctions from "../functions/GlobalFunctions";
import File from "../components/File/File";

export default function SubmittedMaintenanceForms() {
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const getMonthName = (month) => {
        const index = parseInt(month, 10) - 1;
        return index >= 0 && index < 12 ? months[index] : month;
    };

    const fetchSubmittedForms = () => {
        setLoading(true);

        const myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + CookieFunctions.getJWTToken());

        fetch("/api/drivers/maintenance/submitted", {
            method: "GET",
            headers: myHeaders,
        })
            .then((res) => {
                if (res.ok) {
                    return res.json();
                }
                throw new Error("Failed to load submitted maintenance forms");
            })
            .then((data) => {
                setForms(data);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                toast.error(error.message || "Error loading submitted maintenance forms", {
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored",
                });
            });
    };

    useEffect(() => {
        fetchSubmittedForms();
    }, []);

    const viewPdf = (docId) => {
        if (!docId) {
            toast.info("No document available for this form.", {
                position: toast.POSITION.BOTTOM_CENTER,
                theme: "colored",
            });
            return;
        }

        setShowModal(true);
        setLoadingPdf(true);
        setPdfData(null);

        const myHeaders = new Headers();
        myHeaders.append("Authorization", "Bearer " + CookieFunctions.getJWTToken());

        fetch(`/api/drivers/maintenance/pdf/${encodeURIComponent(docId)}`, {
            method: "GET",
            headers: myHeaders,
        })
            .then((res) => {
                if (res.ok) {
                    return res.arrayBuffer();
                }
                throw new Error("Failed to load PDF");
            })
            .then((arrayBuffer) => {
                const bytes = new Uint8Array(arrayBuffer);
                setPdfData({ data: bytes });
                setLoadingPdf(false);
            })
            .catch((error) => {
                setLoadingPdf(false);
                toast.error(error.message || "Error loading PDF", {
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored",
                });
            });
    };

    const closeModal = () => {
        setShowModal(false);
        setPdfData(null);
    };

    const renderYesNo = (value) => (
        <CBadge color={value ? "success" : "secondary"}>{value ? "Yes" : "No"}</CBadge>
    );

    return (
        <>
            <CCard>
                <CCardBody>
                    <CCardTitle className="fs-2">Submitted Maintenance Forms</CCardTitle>
                    <hr />

                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : forms.length === 0 ? (
                        <p>No submitted maintenance forms found.</p>
                    ) : (
                        <CTable responsive hover>
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell>Truck/Trailer Number</CTableHeaderCell>
                                    <CTableHeaderCell>Month</CTableHeaderCell>
                                    <CTableHeaderCell>Year</CTableHeaderCell>
                                    <CTableHeaderCell>Service Date</CTableHeaderCell>
                                    <CTableHeaderCell>Greased</CTableHeaderCell>
                                    <CTableHeaderCell>Oil Change</CTableHeaderCell>
                                    <CTableHeaderCell>Maintenance Done</CTableHeaderCell>
                              
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {forms.map((form, index) => (
                                    <CTableRow key={index}>
                                        <CTableDataCell>{form.truckNumber}</CTableDataCell>
                                        <CTableDataCell>{getMonthName(form.month)}</CTableDataCell>
                                        <CTableDataCell>{form.year}</CTableDataCell>
                                        <CTableDataCell>{globalFunctions.formatDate(form.serviceDate).split(' ')[0]}</CTableDataCell>
                                        <CTableDataCell>{renderYesNo(form.greased)}</CTableDataCell>
                                        <CTableDataCell>{renderYesNo(form.oilChange)}</CTableDataCell>
                                        <CTableDataCell>{renderYesNo(form.hasMaintenanceDone)}</CTableDataCell>
                                        <CTableDataCell>
                                            <CButton
                                                color="primary"
                                                size="sm"
                                                onClick={() => viewPdf(form.synergizeDocId)}
                                                disabled={!form.synergizeDocId}
                                            >
                                                View PDF
                                            </CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>
                    )}
                </CCardBody>
            </CCard>

            <CModal visible={showModal} onClose={closeModal} size="lg" scrollable>
                <CModalHeader>
                    <CModalTitle>Maintenance Form</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {loadingPdf ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : pdfData ? (
                        <File file={pdfData} />
                    ) : (
                        <p>No PDF to display.</p>
                    )}
                </CModalBody>
            </CModal>
        </>
    );
}
